import { TypingIndicatorComponent } from '../partials/typing-indicator/typing-indicator.component';
import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MarkdownComponent } from 'ngx-markdown';
import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { TSourceDetail } from '@core/interfaces/TMessage';

import { SpinnerComponent } from '../../../layout/components/atoms/spinner/spinner.component';
import { Conversation } from '../../infrastructure/models/Conversation';
import { ConversationDataService } from '../../services/conversation-data.service';
import { TAdaptedMessageV2, TAdaptedReferenceV3 } from '../../types/types';

import { ReferenceTransformer } from '../../services/adapters/transformers/reference-transformer';
import { FilePreviewModalComponent } from '../partials/file-preview-modal/file-preview-modal.component';
import { MessageControlsComponent } from '../partials/message-controls/message-controls.component';
import { RetryButtonComponent } from '../partials/message-controls/retry-button/retry-button.component';
import { OriginalContentComponent } from '../partials/original-content/original-content.component';
import { BotQuizAnswersComponent } from '../partials/quiz/bot-quiz-answers.component';
import { ReferencesComponent } from '../partials/references/references.component';
import { SourcePreviewModalComponent } from '../partials/source-preview/modal/source-preview-modal.component';
import { ConversationParagraphsComponent } from './conversation-paragraphs/conversation-paragraphs.component';

@Component({
    selector: 'app-conversation-message',
    templateUrl: './conversation-message.component.html',
    styleUrls: ['./conversation-message.component.scss'],
    standalone: true,
    imports: [
      NgClass,
      MarkdownComponent,
      OriginalContentComponent,
      ConversationParagraphsComponent,
      RetryButtonComponent,
      BotQuizAnswersComponent,
      FilePreviewModalComponent,
      ReferencesComponent,
      MessageControlsComponent,
      SpinnerComponent,
      TypingIndicatorComponent,
    ],
})
export class ConversationMessageComponent implements OnInit, OnDestroy {
  private _sub: Subscription[] = [];
  protected conversation: Conversation | undefined | null;
  protected isWriting = false;
  protected isBusy = false;

  constructor(
    private _conversationDataService: ConversationDataService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this._sub.push(
      this._conversationDataService.conversation$.subscribe(conv => {
        this.conversation = conv;
      }),
      this._conversationDataService.isBusy$.subscribe(isBusy => {
        this.isBusy = isBusy;
      }),
      this._conversationDataService.isWriting$.subscribe(isWriting => {
        this.isWriting = isWriting;
      }),
      this._conversationDataService.referenceProcessCompleted$.pipe(
        filter(value => value.paragraphIndex !== null),
        tap(value => {
          const message = this.conversation?.messages.find(msg => msg._id === value.messageId);
          if (message) {
            const paragraph = message.paragraphs[value.paragraphIndex];
            if (paragraph?.references) {
              paragraph.references = [...paragraph.references];
            }
          }
        })
      ).subscribe()
    );
  }

  openSourcePreviewModal(reference: TAdaptedReferenceV3, message: TAdaptedMessageV2) {
    if (!reference.sourceId) {
      return;
    }
    const referenceForSelectList = this.conversation!.getReferenceFromMessageBySourceId(reference, message);
    const resolvedReferenceDetails =
      referenceForSelectList?.children.map(child => ({
        sourceId: child.sourceId ?? reference.sourceId!,
        embeddingId: child.referenceDto?.embeddingId,
        detail: ReferenceTransformer.getSourceDetailForPanel(
          message,
          this.conversation!,
          child.sourceId ?? reference.sourceId!,
          child.referenceDto?.embeddingId
        ),
      })) ?? [];

    const data = {
      referenceForSelectList,
      selectedReference: reference,
      sourceId: reference.sourceId,
      conversationId: this.conversation?.id,
      resolvedReferenceDetails: resolvedReferenceDetails.filter(
        (item): item is { sourceId: string; embeddingId: string | undefined; detail: TSourceDetail } =>
          !!item.detail
      ),
    };
    this.dialog.open(SourcePreviewModalComponent, {
      data,
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      panelClass: ['source-preview-modal', 'slide-in-right'],
      hasBackdrop: true,
      disableClose: false,
    });
  }

  hasMessageContent(message: TAdaptedMessageV2): boolean {
    const hasAnswerText =
      message.paragraphs &&
      message.paragraphs.length > 0 &&
      message.paragraphs.some(p => p.text && p.text.trim() !== '');
    return hasAnswerText;
  }

  /** Live reasoning stream (SSE `thinkingText`); separate from answer paragraphs. */
  protected hasStreamingThinking(message: TAdaptedMessageV2): boolean {
    return !!message.streamingThinkingText?.trim();
  }

  isFirstUserMessage(message: TAdaptedMessageV2): boolean {
    if (!this.conversation?.messages) return false;

    const userMessages = this.conversation.messages.filter(msg => msg.author === 'user');
    return userMessages.length > 0 && userMessages[0] === message;
  }

  isFirstAssistantMessage(message: TAdaptedMessageV2): boolean {
    if (!this.conversation?.messages) return false;

    const assistantMessages = this.conversation.messages.filter(msg => msg.author === 'assistant');
    return assistantMessages.length > 0 && assistantMessages[0] === message;
  }

  isLastAssistantMessage(message: TAdaptedMessageV2): boolean {
    if (!this.conversation?.messages) return false;

    const assistantMessages = this.conversation.messages.filter(msg => msg.author === 'assistant');
    return (
      assistantMessages.length > 0 && assistantMessages[assistantMessages.length - 1] === message
    );
  }

  /**
   * Check if this is the first quiz message in the conversation
   * Used to hide controls only on the first quiz message
   */
  isFirstQuizMessage(message: TAdaptedMessageV2): boolean {
    if (!this.conversation?.messages || message.type !== 'quiz') return false;

    const quizMessages = this.conversation.messages.filter(msg => msg.type === 'quiz');
    return quizMessages.length > 0 && quizMessages[0] === message;
  }

  /**
   * Determine if controls should be hidden for this message
   */
  shouldHideControls(message: TAdaptedMessageV2): boolean {
    // Always hide controls for error and retry messages
    if (['error', 'retry'].includes(message.type)) {
      return true;
    }
    // Show controls for quiz messages (they're like assistant answers)
    return false;
  }

  ngOnDestroy() {
    this._sub.forEach(sub => sub.unsubscribe());
  }

  log(name: string, value: any): void {
    console.log(name, value);
  }
}
